/**
 * QuickBooks Online Integration API Service
 *
 * Like MYOB and unlike Xero, there are no credentials to save here. QuickBooks
 * uses the OAuth authorization-code flow against a single Wadakaroo app, so the
 * workshop consents in a browser rather than pasting a client id and secret.
 *
 * What this service manages is that consent and what to do with it:
 * - Fetch the consent URL to send the browser to
 * - Read connection status (connected / lapsed / ready to push / sandbox)
 * - Load items, tax codes and bank accounts from the workshop's own company
 * - Save which of those invoices and payments should use
 * - Disconnect
 *
 * No Redux — the AccountingSoftwarePage manages its own async state.
 */

import axiosInstance from './axiosInstance';

/**
 * Where to send the browser for QuickBooks consent.
 *
 * Returns `{ available: false }` on environments with no Intuit app registered,
 * which is a state to render, not an error to throw. `sandbox` says whether this
 * environment points at Intuit's test companies rather than real books.
 *
 * @returns {Promise<{ available: boolean, sandbox: boolean, authorizeUrl?: string, message?: string }>}
 */
export const getQuickBooksAuthorizeUrlApi = async () => {
    const response = await axiosInstance.get('/integrations/quickbooks/authorize-url');
    return response.data;
};

/**
 * Current QuickBooks connection for the authenticated organisation.
 *
 * `needsReconnect` is distinct from `connected: false` — a connection whose
 * consent lapsed needs different words on screen from one that never existed.
 * `readyToPush` means connected AND told which item, tax code and account to use.
 *
 * @returns {Promise<QuickBooksConnectionResponse>}
 */
export const getQuickBooksConnectionApi = async () => {
    const response = await axiosInstance.get('/integrations/quickbooks/connection');
    return response.data;
};

/**
 * Service items, tax codes and bank accounts from the workshop's own company.
 *
 * Always resolves: an Intuit outage comes back as `{ loaded: false, message }`
 * rather than a rejected promise, because the page still has a connection to
 * show around it.
 *
 * @returns {Promise<{ loaded: boolean, needsReconnect: boolean, message?: string,
 *                     items: Array, taxCodes: Array, depositAccounts: Array }>}
 */
export const getQuickBooksReferenceDataApi = async () => {
    const response = await axiosInstance.get('/integrations/quickbooks/reference-data');
    return response.data;
};

/**
 * Choose what invoices and payments use in QuickBooks. All three are IDs from
 * the workshop's own company — none of them mean anything anywhere else.
 *
 * @param {{ itemRef: string, itemName: string, taxCodeRef: string,
 *           taxCodeName: string, depositAccountRef: string,
 *           depositAccountName: string }} defaults
 * @returns {Promise<QuickBooksConnectionResponse>}
 */
export const saveQuickBooksDefaultsApi = async (defaults) => {
    const response = await axiosInstance.put('/integrations/quickbooks/defaults', defaults);
    return response.data;
};

/**
 * Forget the connection. Unlike MYOB, this also asks Intuit to revoke the token,
 * so access really does stop rather than merely being forgotten on our side.
 *
 * @returns {Promise<QuickBooksConnectionResponse>}
 */
export const disconnectQuickBooksApi = async () => {
    const response = await axiosInstance.delete('/integrations/quickbooks/connection');
    return response.data;
};

const quickbooksApi = {
    getQuickBooksAuthorizeUrlApi,
    getQuickBooksConnectionApi,
    getQuickBooksReferenceDataApi,
    saveQuickBooksDefaultsApi,
    disconnectQuickBooksApi,
};

export default quickbooksApi;
